import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { EditProductRootParamList } from "../../../../types";
import React from "react";
import {
  QUERY_PRODUCT,
  SelectedProductEntityContext,
  productEntityToFormData,
  useSelectedProductEntity,
} from "../../utils";
import { useLazyQuery } from "@apollo/client";
import { PageErrorOccurred } from "../../../../components";
import { EditProductScreen } from "../EditProductScreen";
import { ProductFormActionsContext } from "../../components/ProductForm/context";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { ProductFormProvider } from "../../components/ProductForm/ProductFormProvider";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { useActiveLocation } from "../../../../utilities/app-state/hooks";
import { ProductSelectCategoryScreen } from "../ProductSelectCategoryScreen";
import { ProductSelectDepartmentScreen } from "../ProductSelectDepartmentScreen";
import { ProductSelectTaxGroupScreen } from "../ProductSelectTaxGroupScreen";
import { ProductSelectSupplierScreen } from "../ProductSelectSupplierScreen";
import Location from "../../../../utilities/data/model/Location";
import { ProductActionsRootScreen } from "../ProductActionsRootScreen";
import { ManageProductTagsRootScreen } from "../ManageProductTagsRootScreen";
import { ProductSelectUomScreen } from "../ProductSelectUomScreen";
import { PlaceholderLoadingScreen } from "../PlaceholderLoadingScreen";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { useProductFormActionsContextProvider } from "../../components/ProductForm/hooks";
import { EditProductRootScreenContext } from "./context";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { DuplicateProductFormContext } from "../../components/DuplicateProductForm/context";
import { DuplicateProductFormValues } from "../../components/DuplicateProductForm/types";
import { ProductViewSerialNumbersScreen } from "../ProductViewSerialNumbersScreen";
import { ProductViewInventoryLevelsScreen } from "../ProductViewInventoryLevelsScreen";
import { ProductDescriptionLlmQuickWinRootScreen } from "../ProductDescriptionLlmScreen";
import { ProductTagsLlmQuickWinRootScreen } from "../ProductTagsLlmScreen/ProductTagsLlmQuickWinRootScreen";
import { ProductPricingLlmQuickWinRootScreen } from "../ProductPricingLlmScreen";
import { ProductSeoFriendlyNameLlmQuickWinRootScreen } from "../ProductNameSuggestionsLlmScreens/ProductSeoFriendlyNameLlmQuickWinRootScreen";
import { ProductAiInsightsRootModalScreen } from "../../../point-of-sale/screens/ProductAiInsightsScreens";

type Params = {
  EditProductRootScreen: {
    productId: string;
  };
};

export type EditProductRootScreenProps = StackScreenProps<
  Params,
  "EditProductRootScreen"
>;

const EditProductRoot = ({
  product,
  location,
  navigation,
}: EditProductRootScreenProps & {
  product: Product;
  location: Location;
}) => {
  const maybeParentContext = useProductFormActionsContextProvider();
  const selectedProductContext = useSelectedProductEntity(product);
  const Drawer = createStackHeaderNavigator<EditProductRootParamList>();
  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  const defaultValues = productEntityToFormData(product, location.id);

  return (
    <DuplicateProductFormContext.Provider
      value={{
        handleOnSubmit: async (input: DuplicateProductFormValues) => {
          await fetchAndSyncEntity("products");
        },
      }}
    >
      <SelectedProductEntityContext.Provider value={selectedProductContext}>
        <ProductFormActionsContext.Provider
          value={{
            onAfterAddProduct: async (product, _) => {
              await fetchAndSyncEntity("products");
              maybeParentContext?.onAfterAddProduct &&
                (await maybeParentContext.onAfterAddProduct(
                  product,
                  navigation
                ));
              navigation.pop();
            },
          }}
        >
          <ProductFormProvider
            defaultFormValues={defaultValues}
            createActionContext={false}
          >
            <Drawer.Navigator
              screenOptions={{
                animationEnabled: true,
                title: "",
              }}
            >
              <Drawer.Group
                screenOptions={{
                  headerLeft: () => <ModalCloseButton type="Back" />,
                  cardStyleInterpolator:
                    CardStyleInterpolators.forHorizontalIOS,
                }}
              >
                <Drawer.Screen
                  name="EditProductScreen"
                  component={EditProductScreen}
                />
              </Drawer.Group>
              <Drawer.Group
                screenOptions={{
                  headerShown: true,
                  headerLeft: () => <ModalCloseButton type="Cancel" />,
                  cardStyleInterpolator:
                    CardStyleInterpolators.forHorizontalIOS,
                }}
              >
                <Drawer.Screen
                  name="ProductSelectCategoryScreen"
                  component={ProductSelectCategoryScreen}
                />
                <Drawer.Screen
                  name="ProductSelectDepartmentScreen"
                  component={ProductSelectDepartmentScreen}
                />
                <Drawer.Screen
                  name="ProductSelectTaxGroupScreen"
                  component={ProductSelectTaxGroupScreen}
                />
                <Drawer.Screen
                  name="ProductSelectSupplierScreen"
                  component={ProductSelectSupplierScreen}
                />
                <Drawer.Screen
                  name="ProductSelectUomScreen"
                  component={ProductSelectUomScreen}
                />
                <Drawer.Screen
                  name="ProductViewSerialNumbersScreen"
                  component={ProductViewSerialNumbersScreen}
                />
                <Drawer.Screen
                  name="ProductViewInventoryLevelsScreen"
                  component={ProductViewInventoryLevelsScreen}
                />
              </Drawer.Group>
              <Drawer.Group
                screenOptions={{
                  headerShown: false,
                  cardStyleInterpolator:
                    CardStyleInterpolators.forHorizontalIOS,
                }}
              >
                <Drawer.Screen
                  name="ManageProductTagsRootScreen"
                  component={ManageProductTagsRootScreen}
                />
                <Drawer.Screen
                  name="ProductDescriptionLlmQuickWinRootScreen"
                  component={ProductDescriptionLlmQuickWinRootScreen}
                />
                <Drawer.Screen
                  name="ProductTagsLlmQuickWinRootScreen"
                  component={ProductTagsLlmQuickWinRootScreen}
                />
                <Drawer.Screen
                  name="ProductPricingLlmQuickWinRootScreen"
                  component={ProductPricingLlmQuickWinRootScreen}
                />
                <Drawer.Screen
                  name="ProductSeoFriendlyNameLlmQuickWinRootScreen"
                  component={ProductSeoFriendlyNameLlmQuickWinRootScreen}
                />
              </Drawer.Group>
              <Drawer.Group
                screenOptions={{
                  headerShown: false,
                  presentation: "modal",
                }}
              >
                <Drawer.Screen
                  name="ProductActionsRootScreen"
                  component={ProductActionsRootScreen}
                />
                <Drawer.Screen
                  name="ProductAiInsightsRootModalScreen"
                  component={ProductAiInsightsRootModalScreen}
                />
              </Drawer.Group>
            </Drawer.Navigator>
          </ProductFormProvider>
        </ProductFormActionsContext.Provider>
      </SelectedProductEntityContext.Provider>
    </DuplicateProductFormContext.Provider>
  );
};

export const EditProductRootScreen = (props: EditProductRootScreenProps) => {
  const [product, setProduct] = React.useState<Product | undefined>(undefined);
  const { route, navigation } = props;
  const { location } = useActiveLocation();
  const [error, setError] = React.useState<string | undefined>(undefined);
  const transitionEnded = useHasTransitionEnded(navigation);

  const { productId } = route.params || { productId: null };
  const [q] = useLazyQuery(QUERY_PRODUCT);

  React.useEffect(() => {
    loadProduct();
  }, []);

  const loadProduct = React.useCallback(async () => {
    if (!productId) {
      setError("Not found");
      return;
    }
    q({
      variables: {
        id: productId,
      },
    })
      .then((result) => {
        const product = result.data.product;
        if (product) {
          setProduct(product);
        } else {
          setError("Not found");
        }
      })
      .catch((e) => {
        setError("Not found");
      });
  }, [productId, setProduct]);

  if ((!product && error) || !location) {
    return (
      <PageErrorOccurred
        errorMessage={error}
        onRetry={() => {
          navigation.goBack();
        }}
      />
    );
  }

  // Wait for transition to end before rendering the screen
  // otherwise the screen animation will be janky
  if (!product || false == transitionEnded) {
    return (
      <PlaceholderLoadingScreen
        navigation={navigation}
        content={ResourcePageSkeleton}
      />
    );
  }

  return (
    <EditProductRootScreenContext.Provider
      value={{
        refreshProduct: loadProduct,
      }}
    >
      <EditProductRoot {...props} product={product} location={location} />
    </EditProductRootScreenContext.Provider>
  );
};
